import React, { FC, useEffect, useState } from 'react';
import { ProjectListTableProps } from './ProjectListTableProps';
import {
  setupColumnsWidthAndOrder,
  useDatagridSettings,
} from '../../hooks/useDatagridSettings';
import useTranslation from '../../hooks/useTranslation';
import Box from '@mui/material/Box';
import {
  DataGridPremium,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium';
import {
  buildDefaultVisibility,
  calculateStats,
  DEFAULT_VIEW_COLUMNS,
  filterLadaProjects,
  FINANCIAL_VIEW_COLUMNS,
  FORECAST_VIEW_COLUMNS,
  getTableColumns,
  groupColumn,
  returnStatistics,
  CRP_FY_RANGE_OPTIONS,
} from './utils';
import { FY_BUDGET_FILTER_FIELD, FY_OPTIONS } from './toolbar/FYBudgetFilter';
import TableToolBar from '../TableToolBar';
import { getIdFromLabel } from '../../utils/utils';
import Button from '@ingka/button';
import trashcan from '@ingka/ssr-icon/paths/trash-can';
import briefcase from '@ingka/ssr-icon/paths/briefcase';
import RoleRestricted from '../RoleRestricted';
import { AD, SA } from '../RoleRestricted/utils';
import { ProjectStatus } from '../../models/project';
import skapaColours from '@ingka/variables/colours-css';

export const ProjectListTable: FC<ProjectListTableProps> = ({
  projects,
  isLoading,
  tableSelection,
  setTableSelection,
  handleDeleteProject,
  handleArchiveProject,
  handleChangeProjectStatus,
  handleOpenEditProjectModal,
  openPartOfCrpHandler,
  openPartOfApprovedCrpHandler,
  partOfApprovedCrpButtonDisabled,
  bulkEditHandler,
  countries,
  selectedCountries,
  setStats,
  directiveClickedEvent,
}) => {
  const t = useTranslation();

  const columns = getTableColumns(t, countries, directiveClickedEvent);

  const settings = useDatagridSettings('projectList', null, {
    columnsVisibility: buildDefaultVisibility(columns, DEFAULT_VIEW_COLUMNS),
    aggregationModel: {},
    groupingModel: [],
    sizeModel: {},
    orderModel: [],
    filterModel: {
      items: [
        {
          field: 'confidential',
          operator: 'isAnyOf',
          value: ['Internal'],
          id: 'confidential-isAnyOf',
        },
      ],
    },
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const gridApi = settings.datagridProps.apiRef;

  const searchAppliedFilterValues = (key: string) =>
    settings.datagridProps.filterModel.items
      ?.filter((item) => item.field === key)
      .map((item) => item.value)
      ?.flat(1) || [];

  const toggleFilter = ({
    field,
    operator,
    value,
  }: {
    field: string;
    operator: string;
    value: string[];
  }) => {
    const id = `${field}-${operator}`;
    if (value.length === 0) {
      gridApi.current.deleteFilterItem({ id, operator, field });
      return;
    }
    gridApi.current.upsertFilterItem({ field, operator, value, id });
  };

  const [visibleProjects, setVisibleProjects] = useState(projects);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [selectedView, setSelectedView] = useState<string[]>(['default']);

  useEffect(() => {
    if (!gridApi?.current) return;
    const ids = gridFilteredSortedRowIdsSelector(gridApi);
    const rows = ids.map((id) => gridApi.current.getRow(id));
    const stats = calculateStats(
      rows,
      selectedCountries.length === 1 && rows?.[0]?.currencySymbol,
    );
    setStats(
      returnStatistics(stats, selectedCountries.length === 1, rows.length),
    );
  }, [settings.datagridProps.filterModel, visibleProjects, selectedCountries]);

  useEffect(() => {
    setVisibleProjects(projects);
  }, [projects]);

  const viewClicked = (view) => {
    const visibilityObject = {};
    let columnsToShow = [];
    const columnsToHide = columns.map((column) => column.field);
    switch (view) {
      case 'default':
        columnsToShow = DEFAULT_VIEW_COLUMNS;
        setVisibleProjects(projects);
        break;
      case 'financial':
        columnsToShow = FINANCIAL_VIEW_COLUMNS;
        setVisibleProjects(projects);
        break;
      case 'lada':
        columnsToShow = DEFAULT_VIEW_COLUMNS;
        const ladaProjects = filterLadaProjects(projects);
        setVisibleProjects(ladaProjects);
        break;
      case 'forecast':
      default:
        columnsToShow = FORECAST_VIEW_COLUMNS;

        const filteredProjects = projects.filter((project) => {
          const info = project.financialSummaryInfos?.[0];
          return (
            info !== null &&
            info?.totalInvestment > 0 &&
            project.unitCategory !== 'meetingPlace' &&
            ![ProjectStatus.CANCELLED, ProjectStatus.ON_HOLD].includes(
              project.projectStatus as ProjectStatus,
            )
          );
        });

        setVisibleProjects(filteredProjects);
    }
    columnsToHide.forEach((column) => {
      visibilityObject[column] = false;
    });
    columnsToShow.forEach((column) => {
      visibilityObject[column] = true;
    });
    settings.datagridProps.onColumnVisibilityModelChange(visibilityObject);
  };

  const isArchiveButtonVisible = tableSelection.every((projectId) => {
    const project = projects.find((el) => el.id === projectId);
    if (!project) return false;
    return (
      !project?.realEstateSISAN &&
      (project?.projectStatus === ProjectStatus.CANCELLED ||
        project?.projectStatus === ProjectStatus.CLOSED)
    );
  });

  const isChangeStatusButtonVisible = tableSelection.every((projectId) => {
    const project = projects.find((el) => el.id === projectId);
    return !project?.realEstateSISAN;
  });
  const isDeleteButtonVisible =
    tableSelection.length === 1 &&
    tableSelection.every((projectId) => {
      const project = projects.find((el) => el.id === projectId);
      return project?.projectStatus === ProjectStatus.UNQUALIFIED;
    });

  return (
    <Box position="relative" p={2} height="100%">
      <Box position="absolute" sx={{ inset: 0 }} data-testid="ppm-project-list">
        <DataGridPremium
          {...settings.datagridProps}
          columnVisibilityModel={{
            ...settings.datagridProps.columnVisibilityModel,
            [FY_BUDGET_FILTER_FIELD]: false,
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'lastUpdatedDate', sort: 'desc' }],
            },
          }}
          rowHeight={64}
          sx={{
            border: 0,
            '.MuiDataGrid-row:has(.crp-cell--overflow-visible)': {
              overflow: 'visible',
              position: 'relative',
            },
            '.MuiDataGrid-row:has(.crp-cell--overflow-visible):hover': {
              zIndex: 1,
            },
            '.crp-cell--overflow-visible': {
              overflow: 'visible',
            },
            '.MuiDataGrid-row:has(.directive-cell--overflow-visible)': {
              overflow: 'visible',
              position: 'relative',
            },
            '.MuiDataGrid-row:has(.directive-cell--overflow-visible):hover': {
              zIndex: 1,
            },
            '.directive-cell--overflow-visible': {
              overflow: 'visible',
            },
            '.Mui-DatagridRow-even': {
              backgroundColor: skapaColours.colourNeutral2,
            },
            '.MuiDataGrid-columnHeaderTitle': {
              color: skapaColours.colourNeutral6,
            },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? 'Mui-DatagridRow-even'
              : 'Mui-DatagridRow-odd'
          }
          slots={{ toolbar: TableToolBar }}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={setTableSelection}
          rowSelectionModel={tableSelection}
          checkboxSelection
          disableRowSelectionOnClick
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          slotProps={{
            panel: {
              anchorEl: filterAnchorEl,
              placement: 'bottom-end',
            },
            toolbar: {
              setFilterAnchorEl,
              saveSettings: settings.saveSettings,
              resetSettings: settings.resetSettings,
              filters: [
                {
                  label: 'ISEP',
                  options: [
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ],
                  selectedValues: searchAppliedFilterValues('isep'),
                  defaultToggle: true,
                  id: getIdFromLabel('isep'),
                  onToggle: (value) =>
                    toggleFilter({
                      field: 'isep',
                      value: value as string[],
                      operator: 'isAnyOf',
                    }),
                },
                {
                  label: 'Confidentiality',
                  options: [
                    { label: 'Internal', value: 'Internal' },
                    { label: 'Confidential', value: 'Confidential' },
                    {
                      label: 'Strictly confidential',
                      value: 'Strictly confidential',
                    },
                  ],
                  selectedValues: searchAppliedFilterValues('confidential'),
                  defaultToggle: true,
                  id: getIdFromLabel('confidential'),
                  onToggle: (value) =>
                    toggleFilter({
                      field: 'confidential',
                      value: value as string[],
                      operator: 'isAnyOf',
                    }),
                },
                {
                  label: 'FY budget',
                  id: 'fy-budget-filter-menu',
                  options: FY_OPTIONS.map((fy) => ({ value: fy, label: fy })),
                  selectedValues: searchAppliedFilterValues(
                    FY_BUDGET_FILTER_FIELD,
                  ),
                  defaultToggle: true,
                  onToggle: (value) =>
                    toggleFilter({
                      field: FY_BUDGET_FILTER_FIELD,
                      value: value as string[],
                      operator: 'hasAnyFY',
                    }),
                },
                {
                  label: 'CRP',
                  id: 'crp-filter-menu',
                  options: CRP_FY_RANGE_OPTIONS.map((fy) => ({
                    value: fy,
                    label: fy,
                  })),
                  selectedValues: searchAppliedFilterValues('partOfCrpPlan'),
                  defaultToggle: true,
                  onToggle: (value) =>
                    toggleFilter({
                      field: 'partOfCrpPlan',
                      value: value as string[],
                      operator: 'isAnyOf',
                    }),
                },
                {
                  label: t('ppm.columns.subPortfolio'),
                  options: Object.values(
                    (t('ppm.subportfolios') || {}) as Record<string, string>,
                  ).map((label) => ({ value: label, label })),
                  selectedValues: searchAppliedFilterValues('subPortfolio'),
                  defaultToggle: true,
                  limitedHeight: true,
                  onToggle: (value) =>
                    toggleFilter({
                      field: 'subPortfolio',
                      value: value as string[],
                      operator: 'isAnyOf',
                    }),
                  id: getIdFromLabel('subPortfolio'),
                },
                {
                  label: 'Views',
                  options: [
                    { label: 'Default view', value: 'default' },
                    { label: 'Financial view', value: 'financial' },
                    { label: 'Next FY Forecast', value: 'forecast' },
                    { label: 'Lada', value: 'lada' },
                  ],
                  selectedValues: selectedView,
                  onToggle: (value) => {
                    setSelectedView([value]);
                    viewClicked(value);
                  },
                  id: getIdFromLabel('view'),
                },
              ],
              children: tableSelection.length ? (
                <>
                  {isDeleteButtonVisible && (
                    <Button
                      type="secondary"
                      iconOnly
                      ssrIcon={trashcan}
                      size="small"
                      onClick={handleDeleteProject}
                    />
                  )}

                  {isArchiveButtonVisible && (
                    <Button
                      type="secondary"
                      iconOnly
                      ssrIcon={briefcase}
                      size="small"
                      onClick={handleArchiveProject}
                    />
                  )}
                  <Button
                    type="secondary"
                    size="small"
                    data-testid={'bulk-edit'}
                    onClick={bulkEditHandler}
                  >
                    {'Bulk Update'}
                  </Button>
                  {isChangeStatusButtonVisible && (
                    <Button
                      type="secondary"
                      size="small"
                      data-testid={'change-project-status'}
                      onClick={handleChangeProjectStatus}
                    >
                      {t('ppm.actions.changeStatus')}
                    </Button>
                  )}
                  <Button
                    type="secondary"
                    size="small"
                    data-testid={'part-of-crp-edit'}
                    onClick={openPartOfCrpHandler}
                  >
                    {t('ppm.actions.addToCrp')}
                  </Button>
                  <RoleRestricted roles={[AD, SA]}>
                    <Button
                      type="primary"
                      size="small"
                      data-testid={'part-of-approved-crp-edit'}
                      onClick={openPartOfApprovedCrpHandler}
                      disabled={partOfApprovedCrpButtonDisabled}
                    >
                      {t('ppm.actions.addToApprovedCrp')}
                    </Button>
                  </RoleRestricted>
                </>
              ) : undefined,
            },
          }}
          pagination
          onRowClick={(params) => {
            settings.datagridProps.onRowClick(params);
            if (params?.id?.toString().includes('auto-generated-row')) return;
            handleOpenEditProjectModal(params?.row?.referenceId);
          }}
          rowGroupingColumnMode={'multiple'}
          groupingColDef={{
            renderCell: groupColumn,
          }}
          columns={setupColumnsWidthAndOrder(
            columns,
            settings.datagridProps.columnSizeModel,
            settings.datagridProps.columnOrderModel,
          )}
          rows={visibleProjects}
        />
      </Box>
    </Box>
  );
};
